import { Monitor, Moon, Sun } from "lucide-react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type ThemePreference = "system" | "light" | "dark";

const storageKey = "evileye-theme";
const darkQuery = "(prefers-color-scheme: dark)";

interface ThemeContextValue {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function storedPreference(): ThemePreference {
  try {
    const value = localStorage.getItem(storageKey);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark() {
  return typeof window.matchMedia === "function" && window.matchMedia(darkQuery).matches;
}

function applyTheme(preference: ThemePreference) {
  const resolved = preference === "system"
    ? systemPrefersDark() ? "dark" : "light"
    : preference;
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.style.colorScheme = resolved;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", resolved === "dark" ? "#111314" : "#f5f6f5");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(storedPreference);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, preference);
    } catch {
      // The selected theme still applies when storage is unavailable.
    }
    applyTheme(preference);
    if (preference !== "system" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(darkQuery);
    const changed = () => applyTheme("system");
    media.addEventListener("change", changed);
    return () => media.removeEventListener("change", changed);
  }, [preference]);

  return <ThemeContext.Provider value={{ preference, setPreference }}>{children}</ThemeContext.Provider>;
}

function useStandaloneTheme(enabled: boolean): ThemeContextValue {
  const [preference, setPreference] = useState<ThemePreference>(storedPreference);

  useEffect(() => {
    if (!enabled) return;
    try {
      localStorage.setItem(storageKey, preference);
    } catch {
      // The selected theme still applies when storage is unavailable.
    }
    applyTheme(preference);
    if (preference !== "system" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(darkQuery);
    const changed = () => applyTheme("system");
    media.addEventListener("change", changed);
    return () => media.removeEventListener("change", changed);
  }, [enabled, preference]);

  return { preference, setPreference };
}

const options = [
  { value: "system", label: "跟随系统", icon: Monitor },
  { value: "light", label: "亮色模式", icon: Sun },
  { value: "dark", label: "暗色模式", icon: Moon },
] as const;

export function ThemeSwitcher({ className = "" }: { className?: string }) {
  const providedTheme = useContext(ThemeContext);
  const standaloneTheme = useStandaloneTheme(providedTheme === null);
  const theme = providedTheme ?? standaloneTheme;

  return (
    <div className={`theme-switcher${className ? ` ${className}` : ""}`} role="group" aria-label="颜色模式">
      {options.map(({ value, label, icon: Icon }) => (
        <button
          className={theme.preference === value ? "active" : ""}
          type="button"
          key={value}
          title={label}
          aria-label={label}
          aria-pressed={theme.preference === value}
          onClick={() => theme.setPreference(value)}
        >
          <Icon aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
